import type { InjectionKey } from 'vue'
/**
 * Decides what a click on a table body row should do.
 * It is plain TypeScript with no Vue, so the rules can be tested without mounting a table.
 */

/**
 * Elements that have their own job inside a row.
 * A click on one of them, or on anything inside one, never opens the row.
 * A page marks any element the list misses with `data-row-click-ignore`.
 */
export const ROW_CLICK_IGNORE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="combobox"]',
  '[data-row-click-ignore]',
].join(', ')

/**
 * What a row click means.
 * `ignore` does nothing. `toggle` ticks or unticks the row's checkbox. `open` opens the row.
 */
export type RowClickAction = 'ignore' | 'toggle' | 'open'

/** The click target as an element. A click on a text node counts as a click on its parent. */
function targetElement(event: MouseEvent): Element | null {
  const target = event.target
  if (target instanceof Element) return target
  if (target instanceof Node) return target.parentElement
  return null
}

/**
 * True when the click landed in an interactive element that sits inside the row.
 * An interactive element outside the row does not count.
 * Otherwise a table placed inside a `label` would ignore every click.
 */
function isOnInteractiveElementInRow(event: MouseEvent, rowElement: Element): boolean {
  const target = targetElement(event)
  if (!target) return false
  const interactiveAncestor = target.closest(ROW_CLICK_IGNORE_SELECTOR)
  if (!interactiveAncestor || interactiveAncestor === rowElement) return false
  return rowElement.contains(interactiveAncestor)
}

/**
 * True when the click ends a drag that selected text in the row.
 * A selection elsewhere on the page, such as in the peek view, does not count.
 */
function endsTextSelectionInRow(rowElement: Element): boolean {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false
  const { anchorNode, focusNode } = selection
  return (
    (anchorNode !== null && rowElement.contains(anchorNode)) ||
    (focusNode !== null && rowElement.contains(focusNode))
  )
}

/** Cmd, Ctrl or Shift: the keys that select rows instead of opening one. */
export function isSelectionClick(event: MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey
}

/**
 * True when no modifier key was held: not cmd, ctrl, shift or alt.
 * A page's row click handler uses it to open a row on a plain click only.
 * Every page shares it, so they all treat the same keys as modifiers.
 */
export function isPlainClick(event: MouseEvent): boolean {
  return !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

export function classifyRowClick(
  event: MouseEvent,
  rowElement: Element,
  options: { selectable: boolean },
): RowClickAction {
  if (event.button !== 0) return 'ignore'
  if (isOnInteractiveElementInRow(event, rowElement)) return 'ignore'

  // This comes before the selection check on purpose.
  // A shift-click can leave a text range behind, and it must still tick the range of rows.
  if (options.selectable && isSelectionClick(event)) return 'toggle'

  if (!isSelectionClick(event) && endsTextSelectionInRow(rowElement)) return 'ignore'

  return 'open'
}

/**
 * DataTable provides this to the cells it draws: it says whether a click on a given row opens that row in the peek view.
 * The ID cell reads it to hide its own "open in side peek" button, because the row click already opens the same record.
 * Outside a DataTable nothing provides it, so cells keep their button.
 */
export const ROW_OPENS_IN_PEEK: InjectionKey<(row: Record<string, unknown>) => boolean> = Symbol('rowOpensInPeek')
